import { useState, useLayoutEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Edit,
  Trash2,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format, isPast, parseISO } from "date-fns";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// DealTask type (matches crm_x_deal_tasks schema)
interface DealTask {
  id: string;
  dealId: string;
  title: string;
  description?: string | null;
  status: "pending" | "in_progress" | "completed";
  priority: "low" | "medium" | "high" | "urgent";
  startDate?: string | null;
  dueDate?: string | null;
  assignedTo?: string | null;
  assignedToName?: string | null;
  isOverdue?: boolean; // Calculated by API
  createdAt: string;
  updatedAt: string;
}

// Contact type for assignee dropdown
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
}

interface DealTasksPanelProps {
  dealId: string;
}

// Priority badge colors mapping (per spec)
const PRIORITY_BADGE_CLASSES: Record<string, string> = {
  low: "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  medium: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
  high: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
  urgent: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
};

// Task form modal component
interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealId: string;
  editingTask?: DealTask | null;
  onSuccess: () => void;
}

function TaskFormModal({
  open,
  onOpenChange,
  dealId,
  editingTask,
  onSuccess,
}: TaskFormModalProps) {
  const { toast } = useToast();
  const isEditMode = !!editingTask;

  // Form state
  const [title, setTitle] = useState(editingTask?.title || "");
  const [description, setDescription] = useState(editingTask?.description || "");
  const [priority, setPriority] = useState<string>(editingTask?.priority || "medium");
  const [status, setStatus] = useState<string>(editingTask?.status || "pending");
  const [startDate, setStartDate] = useState(editingTask?.startDate || "");
  const [dueDate, setDueDate] = useState(editingTask?.dueDate || "");
  const [assignedTo, setAssignedTo] = useState(editingTask?.assignedTo || "none");

  // Fetch contacts for assignee dropdown
  const { data: contacts = [] } = useQuery<Contact[]>({
    queryKey: ["/api/crm/contacts"],
    enabled: open,
  });

  // Sync form state before browser paint to avoid flash of stale values
  useLayoutEffect(() => {
    if (open && editingTask) {
      setTitle(editingTask.title);
      setDescription(editingTask.description || "");
      setPriority(editingTask.priority);
      setStatus(editingTask.status);
      setAssignedTo(editingTask.assignedTo || "none");
      setStartDate(editingTask.startDate || "");
      setDueDate(editingTask.dueDate || "");
    } else if (open && !editingTask) {
      // Reset for create mode
      setTitle("");
      setDescription("");
      setPriority("medium");
      setStatus("pending");
      setAssignedTo("none");
      setStartDate("");
      setDueDate("");
    }
  }, [open, editingTask]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", `/api/crm/deals/${dealId}/tasks`, {
        title,
        description: description || null,
        priority,
        status,
        assignedTo: assignedTo === "none" ? null : assignedTo,
        startDate: startDate || null,
        dueDate: dueDate || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/tasks`] });
      toast({
        title: "Task created",
        description: "The task has been created successfully.",
      });
      onSuccess();
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create task",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest(
        "PATCH",
        `/api/crm/deals/${dealId}/tasks/${editingTask?.id}`,
        {
          title,
          description: description || null,
          priority,
          status,
          assignedTo: assignedTo === "none" ? null : assignedTo,
          startDate: startDate || null,
          dueDate: dueDate || null,
        }
      );
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/tasks`] });
      toast({
        title: "Task updated",
        description: "The task has been updated successfully.",
      });
      onSuccess();
      handleClose();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task",
        variant: "destructive",
      });
    },
  });

  const handleClose = () => {
    setTitle("");
    setDescription("");
    setPriority("medium");
    setStatus("pending");
    setAssignedTo("none");
    setStartDate("");
    setDueDate("");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      toast({
        title: "Validation error",
        description: "Title is required",
        variant: "destructive",
      });
      return;
    }
    if (isEditMode) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  // Compute display text for assigned contact (avoids Radix Portal timing issue
  // where SelectItems aren't mounted until dropdown opens, so SelectValue can't
  // resolve the matching item text). Also handles the first-render gap before
  // useEffect syncs editingTask.assignedTo into the assignedTo state.
  const selectedContactDisplay = (() => {
    // Check state value first (handles user selections in current session)
    const effectiveId = assignedTo !== "none" ? assignedTo : editingTask?.assignedTo;
    if (!effectiveId) return null;
    const contact = contacts.find((c) => c.id === effectiveId);
    if (contact) return `${contact.firstName} ${contact.lastName}`;
    return editingTask?.assignedToName || null;
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Task" : "Add Task"}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the task details below"
              : "Add a new task to this deal"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              name="title"
              data-testid="input-task-title"
              placeholder="Enter task title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              name="description"
              placeholder="Enter task description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="select-task-priority">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status (only for edit mode) */}
          {isEditMode && (
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="select-task-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Assigned To */}
          <div className="space-y-2">
            <Label htmlFor="assignedTo">Assigned To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger data-testid="select-task-assigned-to">
                {selectedContactDisplay ? (
                  <span className="truncate">{selectedContactDisplay}</span>
                ) : (
                  <SelectValue placeholder="Select assignee..." />
                )}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No assignee</SelectItem>
                {contacts.map((contact) => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.firstName} {contact.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label htmlFor="startDate">Start Date</Label>
            <Input
              id="startDate"
              name="startDate"
              data-testid="input-task-start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          {/* Due Date */}
          <div className="space-y-2">
            <Label htmlFor="dueDate">Due Date</Label>
            <Input
              id="dueDate"
              name="dueDate"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditMode ? "Save" : "Create Task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Task row component
interface TaskRowProps {
  task: DealTask;
  dealId: string;
  onEdit: (task: DealTask) => void;
  onDelete: (task: DealTask) => void;
  onStatusToggle: (task: DealTask) => void;
}

function TaskRow({ task, dealId: _dealId, onEdit, onDelete, onStatusToggle }: TaskRowProps) {
  // Calculate if task is overdue (past due date + not completed)
  const isOverdue =
    task.dueDate &&
    task.status !== "completed" &&
    isPast(parseISO(task.dueDate));

  return (
    <Card
      className="hover:shadow-sm transition-shadow"
      data-testid={`row-task-${task.id}`}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          {/* Status checkbox */}
          <Checkbox
            checked={task.status === "completed"}
            onCheckedChange={() => onStatusToggle(task)}
            className="mt-0.5"
            data-testid={`checkbox-task-${task.id}`}
          />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                {/* Task title */}
                <h4
                  className={`text-sm font-medium ${
                    task.status === "completed"
                      ? "line-through text-muted-foreground"
                      : ""
                  }`}
                >
                  {task.title}
                </h4>

                {/* Description */}
                {task.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {task.description}
                  </p>
                )}
              </div>

              {/* Priority badge */}
              <Badge
                variant="secondary"
                className={`text-xs ${PRIORITY_BADGE_CLASSES[task.priority]}`}
                data-testid={`badge-priority-${task.priority}`}
              >
                {task.priority}
              </Badge>

              {/* Assigned To name */}
              {task.assignedToName && (
                <span className="text-xs text-muted-foreground">
                  {task.assignedToName}
                </span>
              )}
            </div>

            {/* Bottom row: due date, overdue indicator, actions */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {/* Date display: range, start only, or due only */}
                {task.startDate && task.dueDate && (
                  <span
                    className={isOverdue ? "text-red-600 dark:text-red-400" : ""}
                    data-testid="text-date-range"
                  >
                    {format(parseISO(task.startDate), "MMM d")} -{" "}
                    {format(parseISO(task.dueDate), "MMM d, yyyy")}
                  </span>
                )}
                {task.startDate && !task.dueDate && (
                  <span data-testid="text-start-date">
                    {format(parseISO(task.startDate), "MMM d, yyyy")}
                  </span>
                )}
                {!task.startDate && task.dueDate && (
                  <span
                    className={isOverdue ? "text-red-600 dark:text-red-400" : ""}
                    data-testid="text-due-date"
                  >
                    Due: {format(parseISO(task.dueDate), "MMM d, yyyy")}
                  </span>
                )}

                {/* Overdue indicator */}
                {isOverdue && (
                  <span
                    className="flex items-center gap-1 text-red-600 dark:text-red-400"
                    data-testid={`indicator-overdue-${task.id}`}
                  >
                    <AlertCircle className="h-3 w-3" />
                    Overdue
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-0.5">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={() => onEdit(task)}
                  title="Edit task"
                  data-testid={`button-edit-task-${task.id}`}
                >
                  <Edit className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onDelete(task)}
                  title="Delete task"
                  data-testid={`button-delete-task-${task.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main panel component
export function DealTasksPanel({ dealId }: DealTasksPanelProps) {
  const { toast } = useToast();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<DealTask | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<DealTask | null>(null);
  const [completedExpanded, setCompletedExpanded] = useState(false);

  // Fetch tasks from the correct API endpoint
  const { data: tasks = [], isLoading, refetch } = useQuery<DealTask[]>({
    queryKey: [`/api/crm/deals/${dealId}/tasks`],
    enabled: !!dealId,
  });

  // Update task status mutation
  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return await apiRequest("PATCH", `/api/crm/deals/${dealId}/tasks/${id}`, {
        status,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/tasks`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task status",
        variant: "destructive",
      });
    },
  });

  // Delete task mutation
  const deleteMutation = useMutation({
    mutationFn: async (taskId: string) => {
      await apiRequest("DELETE", `/api/crm/deals/${dealId}/tasks/${taskId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/crm/deals/${dealId}/tasks`] });
      toast({
        title: "Task deleted",
        description: "The task has been deleted successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete task",
        variant: "destructive",
      });
    },
  });

  // Group tasks by status
  const inProgressTasks = tasks.filter((t) => t.status === "in_progress");
  const pendingTasks = tasks.filter((t) => t.status === "pending");
  const completedTasks = tasks.filter((t) => t.status === "completed");

  // Handle status toggle
  const handleStatusToggle = (task: DealTask) => {
    const newStatus = task.status === "completed" ? "pending" : "completed";
    // Auto-expand completed section when marking a task as complete
    if (newStatus === "completed") {
      setCompletedExpanded(true);
    }
    statusMutation.mutate({ id: task.id, status: newStatus });
  };

  // Handle edit
  const handleEdit = (task: DealTask) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  // Handle delete click
  const handleDeleteClick = (task: DealTask) => {
    setTaskToDelete(task);
    setIsDeleteDialogOpen(true);
  };

  // Handle delete confirm
  const handleDeleteConfirm = () => {
    if (taskToDelete) {
      deleteMutation.mutate(taskToDelete.id);
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  // Handle modal close
  const handleModalClose = (open: boolean) => {
    if (!open) {
      setEditingTask(null);
    }
    setIsModalOpen(open);
  };

  // Render task section
  const renderTaskSection = (
    tasks: DealTask[],
    sectionTitle: string,
    testIdPrefix: string
  ) => {
    if (tasks.length === 0) return null;

    return (
      <div className="space-y-2" data-testid={`section-tasks-${testIdPrefix}`}>
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {sectionTitle} ({tasks.length})
        </h4>
        {tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            dealId={dealId}
            onEdit={handleEdit}
            onDelete={handleDeleteClick}
            onStatusToggle={handleStatusToggle}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="panel-deal-tasks">
      {/* Header with Add Task button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Deal Tasks</h3>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setIsModalOpen(true)}
          data-testid="button-add-task"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add Task
        </Button>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <Card>
          <CardContent className="pt-3">
            <p className="text-sm text-muted-foreground text-center py-4">
              Loading tasks...
            </p>
          </CardContent>
        </Card>
      ) : tasks.length === 0 ? (
        /* Empty state */
        <Card>
          <CardContent className="pt-3">
            <p className="text-sm text-muted-foreground text-center py-4">
              No tasks for this deal. Click "Add Task" to create one.
            </p>
          </CardContent>
        </Card>
      ) : (
        /* Task sections */
        <div className="space-y-4">
          {/* In Progress section */}
          {renderTaskSection(inProgressTasks, "In Progress", "in-progress")}

          {/* Pending section */}
          {renderTaskSection(pendingTasks, "Pending", "pending")}

          {/* Completed section (collapsible, collapsed by default) */}
          {completedTasks.length > 0 && (
            <Collapsible
              open={completedExpanded}
              onOpenChange={setCompletedExpanded}
              data-testid="section-tasks-completed"
            >
              <CollapsibleTrigger
                className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground transition-colors w-full"
                data-testid="section-tasks-completed-header"
              >
                {completedExpanded ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                Completed ({completedTasks.length})
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-2 mt-2">
                {completedTasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    dealId={dealId}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onStatusToggle={handleStatusToggle}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}

      {/* Task count */}
      <p className="text-xs text-muted-foreground">
        {tasks.length} task{tasks.length !== 1 ? "s" : ""}
        {completedTasks.length > 0 && ` (${completedTasks.length} completed)`}
      </p>

      {/* Add/Edit Task Modal */}
      <TaskFormModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        dealId={dealId}
        editingTask={editingTask}
        onSuccess={() => refetch()}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{taskToDelete?.title}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
